import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { EstadoSinPermiso } from '../components/ui/Estados';
import type { Capacidad } from '../../domain/policies/PoliticaAcceso';
import { useSesionStore } from '../stores/sesionStore';

/**
 * Exige sesión abierta.
 *
 * Si el usuario tiene pendiente un cambio de contraseña obligatorio, no puede
 * ir a ninguna otra parte hasta resolverlo.
 */
export function RequiereSesion({ children }: { children: ReactNode }) {
  const perfil = useSesionStore((s) => s.perfil);
  const ubicacion = useLocation();

  if (!perfil) {
    /*
     * Se recuerda a dónde iba para devolverlo allí tras identificarse, y se
     * arrastra la parte del `?`: hay direcciones que llevan instrucciones ahí
     * —`/?demo` entra solo en la demostración— y perderlas al redirigir
     * dejaba al visitante en la pantalla de acceso sin que pasara nada.
     */
    return (
      <Navigate
        to={{ pathname: '/acceso', search: ubicacion.search }}
        replace
        state={{ destino: ubicacion.pathname }}
      />
    );
  }

  if (perfil.debeCambiarClave && ubicacion.pathname !== '/cambiar-clave') {
    return <Navigate to="/cambiar-clave" replace />;
  }

  return <>{children}</>;
}

/** Impide entrar al área privada a quien ya tiene sesión (páginas de acceso). */
export function RequiereAnonimo({ children }: { children: ReactNode }) {
  const perfil = useSesionStore((s) => s.perfil);
  return perfil ? <Navigate to="/" replace /> : <>{children}</>;
}

/**
 * Exige una capacidad concreta.
 *
 * Se aplica a todas las rutas, no sólo a las de administración: en la versión
 * anterior las pantallas de operación no tenían comprobación y bastaba escribir
 * la dirección para entrar en ellas con cualquier rol.
 */
export function RequiereCapacidad({
  capacidad,
  children,
}: {
  capacidad: Capacidad;
  children: ReactNode;
}) {
  const politica = useSesionStore((s) => s.politica);

  if (!politica?.puede(capacidad)) return <EstadoSinPermiso />;

  return <>{children}</>;
}
